import {
  compact,
  formatPrice,
  humanizeTier,
  IconChevronUp,
  IconQuestion,
  IconX,
  Tooltip,
} from '@leland-dev/leland-ui-library';
import { differenceBy, keyBy, partition, uniq } from 'lodash-es';
import { useRouter } from 'next/router';
import React, {
  type Dispatch,
  type FC,
  type ReactNode,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  GoalName,
  type Scalars,
  SkuTier,
  SpecialBackground,
} from '../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../context/AuthContext';
import { categorySlugToUrn, getSubCategoryTitle } from '../../utils/category';
import {
  COLLEGE_CATEGORY_URN,
  FEATURED_FILTERS,
  FEATURED_FILTERS_ITEMS,
  FEATURED_SCHOOL_FILTERS,
  FEATURED_SCHOOL_FILTERS_SCHOOL_NAMES,
  LIBRARY_URL,
  MBA_CATEGORY_SLUG,
  TARGET_SCHOOLS,
} from '../../utils/constants';
import { formatGoalForUrl } from '../../utils/formatters';
import type { SearchFiltersVariables } from '../../utils/search';
import { getTierDescription } from '../../utils/tier';
import { type SchoolFilterHookOutput } from '../filters/SchoolFilters';
import { type GenericInputOption, type InputOption } from '../forms/Inputs';
import CheckboxInput from '../inputs/CheckboxInput';
import RadioInput from '../inputs/RadioInput';
import TextInput from '../inputs/TextInput';
import { type SelectOptionType } from '../inputs/selects';
import Select from '../inputs/selects/Select';
import { SingleCategoryTypeahead } from '../typeahead/CategoryTypeahead';
import {
  type CompanyTypeaheadCompany,
  MultiCompanyTypeahead,
} from '../typeahead/CompanyTypeahead';
import { MultiSchoolTypeahead } from '../typeahead/SchoolTypeahead';

import {
  type SearchFiltersCategoryFragment,
  type SearchFiltersCoachingFocusFragment,
  type SearchFiltersSkuFragment,
  type SearchFiltersSubCategoryFragment,
  useSearchFiltersGetCompaniesLazyQuery,
} from './__generated-gql-types__/SearchFilters.generated';

const HOURLY_RATE_LOW_VALUE = 50;
const HOURLY_RATE_MEDIUM_VALUES = {
  start: 51,
  end: 150,
};
const HOURLY_RATE_HIGH_VALUE = 151;

enum HOURLY_RATE_SUGGESTIONS_RADIO_VALUES {
  LOW = 'Low',
  MEDIUM = 'Medium',
  HIGH = 'High',
}

type HourlyRateFilterOptions = Record<
  HOURLY_RATE_SUGGESTIONS_RADIO_VALUES,
  boolean
>;

const DEFAULT_HOURLY_RATE_FILTER_OPTIONS: HourlyRateFilterOptions = {
  [HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.LOW]: false,
  [HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.MEDIUM]: false,
  [HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.HIGH]: false,
};

const HourlyRateSuggestionLabel: FC<{
  label: string;
  description: string;
}> = ({ label, description }) => (
  <>
    {label} <span className="text-leland-gray-extra-light">{description}</span>
  </>
);
const HOURLY_RATE_SUGGESTIONS_OPTIONS: Array<
  GenericInputOption<HOURLY_RATE_SUGGESTIONS_RADIO_VALUES>
> = [
  {
    value: HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.LOW,
    label: (
      <HourlyRateSuggestionLabel
        label={HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.LOW}
        description={`Less than $${HOURLY_RATE_LOW_VALUE + 1}/hr`}
      />
    ),
  },
  {
    value: HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.MEDIUM,
    label: (
      <HourlyRateSuggestionLabel
        label={HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.MEDIUM}
        description={`$${HOURLY_RATE_MEDIUM_VALUES.start} – $${HOURLY_RATE_MEDIUM_VALUES.end}/hr`}
      />
    ),
  },
  {
    value: HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.HIGH,
    label: (
      <HourlyRateSuggestionLabel
        label={HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.HIGH}
        description={`More than $${HOURLY_RATE_HIGH_VALUE - 1}/hr`}
      />
    ),
  },
];

const EMPTY_COMPANIES_FILTER: never[] = [];
const EMPTY_LIST_FILTER: never[] = [];

export interface SearchFiltersProps {
  isDirty: boolean;
  category: Possible<SearchFiltersCategoryFragment>;
  searchVariables: SearchFiltersVariables;
  setSearchVariables: Dispatch<SetStateAction<SearchFiltersVariables>>;
  resetVariables: () => void;
  className?: string;
  headerClassName?: string;
  onClose?: () => void;
  schoolFilters: SchoolFilterHookOutput;
  hidePrice: boolean;
  skusData: SearchFiltersSkuFragment[];
}

const ALL_COACHES_VALUE = 'ALL_COACHES';

const SearchFilters: FC<SearchFiltersProps> = ({
  isDirty,
  category,
  searchVariables,
  setSearchVariables,
  resetVariables,
  className = '',
  headerClassName = '',
  onClose,
  schoolFilters: { selectedSchools, addSchoolFilters, removeSchoolFilters },
  hidePrice,
  skusData,
}) => {
  const router = useRouter();
  const { currentUser } = useAuth();

  const [showMoreSchools, setShowMoreSchools] = useState(false);
  const [showMoreServices, setShowMoreServices] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<
    Nullable<SearchFiltersCategoryFragment>
  >(category ?? null);

  const tierInputValueToSkuTierFilter: Record<SkuTier, SkuTier[]> = {
    [SkuTier.BASE]: [SkuTier.BASE],
    [SkuTier.PRO]: [SkuTier.PRO],
    [SkuTier.ELITE]: [SkuTier.ELITE, SkuTier.ELITE_PLUS],
    [SkuTier.ELITE_PLUS]: [SkuTier.ELITE_PLUS],
  };

  const coachTierOptions: InputOption[] = useMemo(() => {
    const basePrice = skusData.find(
      (sku) => sku.tier === SkuTier.BASE,
    )?.unitPrice;
    const proPrice = skusData.find(
      (sku) => sku.tier === SkuTier.PRO,
    )?.unitPrice;
    const elitePrice = skusData.find(
      (sku) => sku.tier === SkuTier.ELITE,
    )?.unitPrice;

    return [
      {
        value: ALL_COACHES_VALUE,
        label: 'Show all coaches',
      },
      {
        value: SkuTier.BASE,
        label: (
          <div className="flex items-center gap-2">
            <span>{humanizeTier(SkuTier.BASE)}</span>
            {basePrice ? (
              <>
                <span className="text-leland-gray-extra-light">&middot;</span>
                <span className="text-leland-gray-extra-light">
                  Starting at {formatPrice(basePrice)}
                </span>
                <Tooltip
                  content={getTierDescription(SkuTier.BASE, category?.goal)}
                >
                  <IconQuestion className="size-4 text-leland-gray-extra-light" />
                </Tooltip>
              </>
            ) : null}
          </div>
        ),
      },
      {
        value: SkuTier.PRO,
        label: (
          <div className="flex items-center gap-2">
            <span>{humanizeTier(SkuTier.PRO)}</span>
            {proPrice ? (
              <>
                <span className="text-leland-gray-extra-light">&middot;</span>
                <span className="text-leland-gray-extra-light">
                  Starting at {formatPrice(proPrice)}
                </span>
                <Tooltip
                  content={getTierDescription(SkuTier.PRO, category?.goal)}
                >
                  <IconQuestion className="size-4 text-leland-gray-extra-light" />
                </Tooltip>
              </>
            ) : null}
          </div>
        ),
      },
      {
        value: SkuTier.ELITE,
        label: (
          <div className="flex items-center gap-2">
            <span>{humanizeTier(SkuTier.ELITE)}</span>
            {elitePrice ? (
              <>
                <span className="text-leland-gray-extra-light">&middot;</span>
                <span className="text-leland-gray-extra-light">
                  Starting at {formatPrice(elitePrice)}
                </span>
                <Tooltip
                  content={getTierDescription(SkuTier.ELITE, category?.goal)}
                >
                  <IconQuestion className="size-4 text-leland-gray-extra-light" />
                </Tooltip>
              </>
            ) : null}
          </div>
        ),
      },
    ];
  }, [category, skusData]);

  // keep `selectedCategory` synced with upstream
  useEffect(() => setSelectedCategory(category ?? null), [category]);

  const { hourlyRateMax, hourlyRateMin } = searchVariables;
  const hourlyRateFilterOptions: HourlyRateFilterOptions = useMemo(() => {
    const options: Partial<HourlyRateFilterOptions> = {};
    if (
      (hourlyRateMin == null || hourlyRateMin === 0) &&
      hourlyRateMax === HOURLY_RATE_LOW_VALUE
    ) {
      options[HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.LOW] = true;
    } else if (
      hourlyRateMin === HOURLY_RATE_MEDIUM_VALUES.start &&
      hourlyRateMax === HOURLY_RATE_MEDIUM_VALUES.end
    ) {
      options[HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.MEDIUM] = true;
    } else if (
      hourlyRateMin === HOURLY_RATE_HIGH_VALUE &&
      hourlyRateMax == null
    ) {
      options[HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.HIGH] = true;
    }

    return {
      ...DEFAULT_HOURLY_RATE_FILTER_OPTIONS,
      ...options,
    };
  }, [hourlyRateMax, hourlyRateMin]);

  const selectedIsMBA =
    selectedCategory?.id === categorySlugToUrn(MBA_CATEGORY_SLUG);
  const selectedIsCollege = selectedCategory?.id === COLLEGE_CATEGORY_URN;

  const subCategoryOptions: Array<
    SelectOptionType<SearchFiltersSubCategoryFragment['id']>
  > = useMemo(
    () =>
      (selectedCategory?.shouldShowSubCategories &&
        selectedCategory?.subCategoryList?.map((subCategory) => ({
          value: subCategory.slug,
          label: subCategory.name,
        }))) ||
      [],
    [selectedCategory],
  );
  const selectedSubCategorySlug = searchVariables.subCategory ?? null;

  const checkedSchools: Record<string, boolean> = useMemo(
    () =>
      selectedSchools.reduce<Record<string, boolean>>((map, { id }) => {
        map[id] = true;
        return map;
      }, {}),
    [selectedSchools],
  );

  const areAllSchoolsChecked = useCallback(
    (schoolUrns: Array<Scalars['SchoolUrn']['output']>) => {
      return schoolUrns.every((schoolUrn) => !!checkedSchools[schoolUrn]);
    },
    [checkedSchools],
  );
  const selectedSpecialBackgrounds =
    searchVariables.specialBackgroundsFilter ?? EMPTY_LIST_FILTER;

  const [selectedCompaniesCache, setSelectedCompaniesCache] = useState<
    Record<string, CompanyTypeaheadCompany>
  >({});
  const selectedCompanyIds =
    searchVariables.companyFilter ?? EMPTY_COMPANIES_FILTER;
  const [cachedCompanyIds, uncachedCompanyIds] = useMemo(
    () => partition(selectedCompanyIds, (id) => !!selectedCompaniesCache[id]),
    [selectedCompaniesCache, selectedCompanyIds],
  );
  const selectedCompanies = useMemo(() => {
    return cachedCompanyIds
      .map((id) => selectedCompaniesCache[id] ?? null)
      .filter(compact);
  }, [cachedCompanyIds, selectedCompaniesCache]);

  const addCompanyFilter = useCallback(
    (companyId: string) => {
      const companyFilter = uniq([...selectedCompanyIds, companyId]);
      setSearchVariables((prev) => ({ ...prev, companyFilter }));
    },
    [selectedCompanyIds, setSearchVariables],
  );
  const removeCompanyFilter = useCallback(
    (companyId: string) => {
      const companyFilter = uniq(
        selectedCompanyIds.filter((id) => id !== companyId),
      );
      setSearchVariables((prev) => ({
        ...prev,
        companyFilter: companyFilter.length > 0 ? companyFilter : null,
      }));
    },
    [selectedCompanyIds, setSearchVariables],
  );

  const addSpecialBackgroundFilter = useCallback(
    (specialBackground: SpecialBackground) => {
      const specialBackgroundsFilter = uniq([
        ...selectedSpecialBackgrounds,
        specialBackground,
      ]);
      setSearchVariables((prev) => ({ ...prev, specialBackgroundsFilter }));
    },
    [selectedSpecialBackgrounds, setSearchVariables],
  );
  const removeSpecialBackgroundFilter = useCallback(
    (specialBackground: SpecialBackground) => {
      const specialBackgroundsFilter = uniq(
        selectedSpecialBackgrounds.filter((sp) => sp !== specialBackground),
      );
      setSearchVariables((prev) => ({
        ...prev,
        specialBackgroundsFilter:
          specialBackgroundsFilter.length > 0
            ? specialBackgroundsFilter
            : undefined,
      }));
    },
    [selectedSpecialBackgrounds, setSearchVariables],
  );

  // decorate selected companies if not yet in cache
  const [lazyGetCompanies] = useSearchFiltersGetCompaniesLazyQuery();
  useEffect(() => {
    let isCancelled = false;
    const asyncGetCompanies = async () => {
      if (uncachedCompanyIds.length) {
        const { data: fetchedCompanies } = await lazyGetCompanies({
          variables: { ids: uncachedCompanyIds },
        });

        if (!isCancelled && fetchedCompanies?.companiesByIds) {
          setSelectedCompaniesCache((prev) => ({
            ...prev,
            ...keyBy(fetchedCompanies.companiesByIds, 'id'),
          }));
        }
      }
    };
    void asyncGetCompanies();

    return () => {
      isCancelled = true;
    };
  }, [lazyGetCompanies, uncachedCompanyIds]);

  return (
    <div className={`${className} h-full`}>
      <div
        className={`fixed top-0 grid h-20 w-full auto-cols-fr grid-flow-col gap-4 border-b border-leland-gray-stroke bg-white p-4 lg:relative lg:mt-4 lg:flex lg:min-h-fit lg:flex-row-reverse lg:items-center lg:justify-between lg:border-none lg:bg-transparent lg:p-0 ${headerClassName}`}
      >
        <button
          className={`flex items-center justify-start text-lg font-medium text-leland-gray-light underline lg:text-base lg:text-leland-primary lg:no-underline ${
            !isDirty ? 'cursor-default opacity-30' : ''
          }`}
          onClick={() => {
            resetVariables();
            if (onClose) {
              onClose();
            }
          }}
          disabled={!isDirty}
        >
          Clear
        </button>
        <h3 className="text-leland-gray flex items-center justify-center text-lg font-medium">
          Filters
        </h3>
        {onClose != null ? (
          <button
            className="ml-2 flex items-center justify-end"
            onClick={onClose}
          >
            <IconX className="size-4 text-leland-gray-dark" />
          </button>
        ) : null}
      </div>
      <hr className="mt-4 hidden lg:block" />
      <div className="mt-20 py-6 lg:mt-0">
        {!category ? (
          <FilterDropdownSection title="Category">
            <SingleCategoryTypeahead
              className="md:basis-2/3"
              label="Category"
              labelIsHidden
              placeholder="Search for a category..."
              value={selectedCategory ?? null}
              onChange={async (category) => {
                if (category) {
                  setSelectedCategory(category);
                  await router.push(
                    `/${formatGoalForUrl(category.goal)}/${
                      category.slug
                    }?keyword=${searchVariables.keyword}`,
                  );
                }
              }}
            />
          </FilterDropdownSection>
        ) : null}
        {selectedCategory?.subCategoryList?.length &&
        selectedCategory.shouldShowSubCategories ? (
          <FilterDropdownSection
            title={getSubCategoryTitle(selectedCategory?.subCategoryTitle)}
          >
            <Select<SearchFiltersSubCategoryFragment['slug']>
              label={getSubCategoryTitle(selectedCategory?.subCategoryTitle)}
              labelIsHidden
              onChange={(subCategorySlug) => {
                if (subCategorySlug) {
                  setSearchVariables((oldVariables) => ({
                    ...oldVariables,
                    subCategory:
                      selectedSubCategorySlug === subCategorySlug
                        ? null
                        : subCategorySlug,
                    shouldDeepRoute: true,
                  }));
                }
              }}
              value={selectedSubCategorySlug}
              options={subCategoryOptions}
            />
          </FilterDropdownSection>
        ) : null}
        {currentUser ? (
          hidePrice ? (
            <FilterDropdownSection title="Coach tier">
              <div className="flex flex-col gap-4">
                {coachTierOptions.map((coachTier) => {
                  return (
                    <RadioInput
                      name="filterTier"
                      option={coachTier}
                      key={coachTier.value}
                      onChange={() => {
                        setSearchVariables((oldVariables) => ({
                          ...oldVariables,
                          skuTiers:
                            coachTier.value === ALL_COACHES_VALUE
                              ? null
                              : tierInputValueToSkuTierFilter[
                                  coachTier.value as SkuTier
                                ],
                        }));
                      }}
                    />
                  );
                })}
              </div>
            </FilterDropdownSection>
          ) : (
            <FilterDropdownSection title="Hourly Rate">
              <div className="mb-4 grid grid-cols-2 gap-2">
                <TextInput
                  inputContainerClassName="w-full"
                  label="Min hourly rate"
                  labelIsHidden
                  placeholder="Min"
                  value={hourlyRateMin}
                  onChangeValue={(newValue) => {
                    let cleanedNewValue: number | null = Number(newValue);
                    if (newValue === '' || isNaN(cleanedNewValue)) {
                      cleanedNewValue = null;
                    }
                    if (cleanedNewValue != null && cleanedNewValue < 0) {
                      cleanedNewValue = 0;
                    }

                    setSearchVariables((oldVariables) => ({
                      ...oldVariables,
                      hourlyRateMin: cleanedNewValue,
                    }));
                  }}
                  subtlePrefix="$"
                  type="number"
                  size={3}
                />
                <TextInput
                  inputContainerClassName="w-full"
                  label="Max hourly rate"
                  labelIsHidden
                  placeholder="Max"
                  value={hourlyRateMax}
                  onChangeValue={(newValue) => {
                    let cleanedNewValue: number | null = Number(newValue);
                    if (newValue === '' || isNaN(cleanedNewValue)) {
                      cleanedNewValue = null;
                    }
                    if (cleanedNewValue != null && cleanedNewValue < 0) {
                      cleanedNewValue = 0;
                    }

                    setSearchVariables((oldVariables) => ({
                      ...oldVariables,
                      hourlyRateMax: cleanedNewValue,
                    }));
                  }}
                  subtlePrefix="$"
                  type="number"
                  size={3}
                />
              </div>
              {HOURLY_RATE_SUGGESTIONS_OPTIONS.map(({ label, value }) => {
                const isChecked = hourlyRateFilterOptions[value];
                return (
                  <div key={value} className="mb-4 last:mb-0">
                    <CheckboxInput
                      label={label}
                      isChecked={isChecked}
                      onToggle={() => {
                        let hourlyRateMin: Nullable<number> = null;
                        let hourlyRateMax: Nullable<number> = null;
                        if (!isChecked) {
                          switch (value) {
                            case HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.LOW:
                              hourlyRateMax = HOURLY_RATE_LOW_VALUE;
                              break;
                            case HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.MEDIUM:
                              hourlyRateMin = HOURLY_RATE_MEDIUM_VALUES.start;
                              hourlyRateMax = HOURLY_RATE_MEDIUM_VALUES.end;
                              break;
                            case HOURLY_RATE_SUGGESTIONS_RADIO_VALUES.HIGH:
                              hourlyRateMin = HOURLY_RATE_HIGH_VALUE;
                              break;
                          }
                        }
                        setSearchVariables((oldVariables) => ({
                          ...oldVariables,
                          hourlyRateMin,
                          hourlyRateMax,
                        }));
                      }}
                    />
                  </div>
                );
              })}
            </FilterDropdownSection>
          )
        ) : null}
        {selectedCategory?.goal === GoalName.SCHOOL ? (
          <FilterDropdownSection title="Schools">
            <>
              <MultiSchoolTypeahead
                className="-mt-2 w-full"
                label="Schools"
                labelIsHidden
                values={selectedSchools}
                categoryUrn={selectedCategory?.id}
                onChange={async (items) => {
                  const addedItem = differenceBy(
                    items,
                    selectedSchools,
                    'id',
                  )[0];
                  if (addedItem) {
                    await addSchoolFilters({ schools: [addedItem] });
                  }

                  const removedItem = differenceBy(
                    selectedSchools,
                    items,
                    'id',
                  )[0];
                  if (removedItem) {
                    return removeSchoolFilters([removedItem.id]);
                  }
                }}
              />
              {/* hide MBA target schools list for non-MBA school categories */}
              {selectedIsMBA ? (
                <div className="mt-4">
                  {Object.values(TARGET_SCHOOLS)
                    .slice(0, showMoreSchools ? undefined : 5)
                    .map((schoolVal) => (
                      <CheckboxInput
                        key={schoolVal.id}
                        label={schoolVal.name}
                        containerClassName="mb-4 last:mb-0"
                        isChecked={!!checkedSchools[schoolVal.id]}
                        onToggle={async (newValue) => {
                          if (newValue) {
                            await addSchoolFilters({ schools: [schoolVal] });
                          } else {
                            removeSchoolFilters([schoolVal.id]);
                          }
                        }}
                      />
                    ))}
                  <ShowMoreButton
                    showMore={showMoreSchools}
                    onClick={() =>
                      setShowMoreSchools((prevState) => !prevState)
                    }
                  />
                </div>
              ) : null}
              {selectedIsCollege ? (
                <div className="mt-4">
                  {FEATURED_FILTERS[COLLEGE_CATEGORY_URN].map((filterName) =>
                    filterName === FEATURED_SCHOOL_FILTERS.COMMON_APP ? null : (
                      <CheckboxInput
                        key={filterName}
                        label={FEATURED_SCHOOL_FILTERS_SCHOOL_NAMES[filterName]}
                        containerClassName="mb-4 last:mb-0"
                        isChecked={areAllSchoolsChecked(
                          FEATURED_FILTERS_ITEMS[filterName],
                        )}
                        onToggle={async (newValue) => {
                          if (newValue) {
                            await addSchoolFilters({
                              schoolIds: FEATURED_FILTERS_ITEMS[filterName],
                            });
                          } else {
                            removeSchoolFilters(
                              FEATURED_FILTERS_ITEMS[filterName],
                            );
                          }
                        }}
                      />
                    ),
                  )}
                </div>
              ) : null}
            </>
          </FilterDropdownSection>
        ) : null}
        {selectedCategory?.goal === GoalName.CAREER ||
        selectedCategory?.goal === GoalName.SCHOOL ? (
          <FilterDropdownSection title="Companies">
            <MultiCompanyTypeahead
              className="-mt-2 w-full"
              label="Companies"
              labelIsHidden
              values={selectedCompanies}
              categoryUrn={selectedCategory?.id}
              onlyUseDB={true}
              onChange={(items) => {
                setSelectedCompaniesCache(keyBy(items, 'id'));
                const addedItem = differenceBy(
                  items,
                  selectedCompanies,
                  'id',
                )[0];
                if (addedItem?.id) {
                  return addCompanyFilter(addedItem.id);
                }

                const removedItem = differenceBy(
                  selectedCompanies,
                  items,
                  'id',
                )[0];
                if (removedItem?.id) {
                  return removeCompanyFilter(removedItem.id);
                }
              }}
            />
          </FilterDropdownSection>
        ) : null}
        <FilterDropdownSection title="Specialized Experience">
          <CheckboxInput
            label="Professional Coach"
            containerClassName="mb-4"
            isChecked={!!searchVariables.professionalCoachFilter}
            onToggle={(newValue) => {
              setSearchVariables((oldVariables) => ({
                ...oldVariables,
                professionalCoachFilter: newValue || null,
              }));
            }}
          />
          <CheckboxInput
            label="Non-U.S. Clients"
            containerClassName="mb-4"
            isChecked={
              !!searchVariables.specialBackgroundsFilter?.includes(
                SpecialBackground.INTERNATIONAL,
              )
            }
            onToggle={(newValue) => {
              if (newValue) {
                addSpecialBackgroundFilter(SpecialBackground.INTERNATIONAL);
              } else {
                removeSpecialBackgroundFilter(SpecialBackground.INTERNATIONAL);
              }
            }}
          />
          {selectedCategory?.goal === GoalName.SCHOOL ? (
            <>
              <CheckboxInput
                label="Ex-Admissions Committee"
                containerClassName="mb-4"
                isChecked={!!searchVariables.hasAdmissionCommitteeFilter}
                onToggle={(newValue) => {
                  setSearchVariables((oldVariables) => ({
                    ...oldVariables,
                    hasAdmissionCommitteeFilter: newValue || null,
                  }));
                }}
              />
              <CheckboxInput
                label="First Generation"
                containerClassName="mb-4"
                isChecked={
                  !!searchVariables.specialBackgroundsFilter?.includes(
                    SpecialBackground.FIRST_GENERATION,
                  )
                }
                onToggle={(newValue) => {
                  if (newValue) {
                    addSpecialBackgroundFilter(
                      SpecialBackground.FIRST_GENERATION,
                    );
                  } else {
                    removeSpecialBackgroundFilter(
                      SpecialBackground.FIRST_GENERATION,
                    );
                  }
                }}
              />
            </>
          ) : null}
          <CheckboxInput
            label="Veterans"
            containerClassName="mb-4"
            isChecked={
              !!searchVariables.specialBackgroundsFilter?.includes(
                SpecialBackground.VETERAN,
              )
            }
            onToggle={(newValue) => {
              if (newValue) {
                addSpecialBackgroundFilter(SpecialBackground.VETERAN);
              } else {
                removeSpecialBackgroundFilter(SpecialBackground.VETERAN);
              }
            }}
          />
          <CheckboxInput
            label="LGBTQ+"
            containerClassName="mb-4"
            isChecked={
              !!searchVariables.specialBackgroundsFilter?.includes(
                SpecialBackground.LGBTQ,
              )
            }
            onToggle={(newValue) => {
              if (newValue) {
                addSpecialBackgroundFilter(SpecialBackground.LGBTQ);
              } else {
                removeSpecialBackgroundFilter(SpecialBackground.LGBTQ);
              }
            }}
          />
          <CheckboxInput
            label="Low Income"
            containerClassName="mb-4"
            isChecked={
              !!searchVariables.specialBackgroundsFilter?.includes(
                SpecialBackground.LOW_INCOME,
              )
            }
            onToggle={(newValue) => {
              if (newValue) {
                addSpecialBackgroundFilter(SpecialBackground.LOW_INCOME);
              } else {
                removeSpecialBackgroundFilter(SpecialBackground.LOW_INCOME);
              }
            }}
          />
        </FilterDropdownSection>

        {category?.coachingFocusList?.length ? (
          <FilterDropdownSection title="Services Offered">
            {category.coachingFocusList
              .slice(0, showMoreServices ? undefined : 5)
              .map((coachingFocus) => (
                <CheckboxInput
                  key={coachingFocus.id}
                  label={coachingFocus.name}
                  isChecked={
                    searchVariables.coachingFocusListFilter?.includes(
                      coachingFocus.id,
                    ) ?? false
                  }
                  onToggle={() => {
                    let newFocuses = [
                      ...(searchVariables.coachingFocusListFilter ?? []),
                      coachingFocus.id,
                    ];
                    if (
                      searchVariables.coachingFocusListFilter?.includes(
                        coachingFocus.id,
                      )
                    ) {
                      newFocuses =
                        searchVariables?.coachingFocusListFilter.filter(
                          (currFocus) => currFocus !== coachingFocus.id,
                        );
                    }
                    setSearchVariables((oldVariables) => ({
                      ...oldVariables,
                      coachingFocusListFilter:
                        newFocuses.length > 0 ? newFocuses : null,
                    }));
                  }}
                  containerClassName="mb-4 last:mb-0"
                  tooltip={
                    coachingFocus.description ? (
                      <CoachingFocusNotes coachingFocus={coachingFocus} />
                    ) : null
                  }
                />
              ))}
            <ShowMoreButton
              showMore={showMoreServices}
              onClick={() => setShowMoreServices((prevState) => !prevState)}
            />
          </FilterDropdownSection>
        ) : null}
      </div>
    </div>
  );
};

interface FilterDropdownSectionProps {
  title: string;
  children?: ReactNode;
  initialIsOpen?: boolean;
}

const FilterDropdownSection: FC<FilterDropdownSectionProps> = ({
  title,
  children,
  initialIsOpen = true,
}) => {
  const [isOpen, setIsOpen] = useState(initialIsOpen);

  return (
    <div className="px-4 pb-6 lg:px-0">
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          setIsOpen((old) => !old);
        }}
        onKeyPress={(e) => {
          if (e.code === 'Space') setIsOpen((old) => !old);
        }}
        className={`mb-6 flex cursor-pointer items-center justify-between ${
          isOpen ? '' : 'lg:mb-0'
        }`}
      >
        <h4 className="select-none text-base font-medium text-leland-gray-dark">
          {title}
        </h4>
        <div className="leading-none">
          <div
            className={`transition-transform ${!isOpen ? 'rotate-180' : ''}`}
          >
            <IconChevronUp className="size-4 text-leland-gray-light" />
          </div>
        </div>
      </div>
      <div className={isOpen ? 'max-h-full' : 'hidden'}>{children}</div>
      <hr className={isOpen ? 'mt-6' : 'mt-2.5 lg:mt-6'} />
    </div>
  );
};

interface ShowMoreButtonProps {
  showMore: boolean;
  onClick: () => void;
}

const ShowMoreButton: FC<ShowMoreButtonProps> = ({ showMore, onClick }) => {
  return (
    <button
      type="button"
      className="mt-4 flex items-center text-base font-medium text-leland-primary"
      onClick={onClick}
    >
      Show {showMore ? 'less' : 'more'}
      <IconChevronUp
        className={`ml-1 size-4 ${!showMore ? 'rotate-180' : ''}`}
      />
    </button>
  );
};

export default SearchFilters;

const CoachingFocusNotes: FC<{
  coachingFocus: SearchFiltersCoachingFocusFragment;
}> = ({ coachingFocus }) => {
  return coachingFocus.description ? (
    <>
      {coachingFocus.description}{' '}
      {coachingFocus.libraryPath ? (
        <a
          href={`${LIBRARY_URL}${coachingFocus.libraryPath}}`}
          className="link link--primary"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more here.
        </a>
      ) : null}
    </>
  ) : (
    ''
  );
};
